// Display helpers for the render-activity board. Absent values render
// as em-dash — never coerce null/undefined into 0/false/guessed text.

/** Format a duration in seconds as a compact relative string (e.g. `3m 20s`). */
export function formatDurationSec(sec: number | null | undefined): string {
  if (sec == null || !Number.isFinite(sec)) return '—';
  const s = Math.max(0, Math.floor(sec));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const rem = s % 60;
  if (m < 60) return rem > 0 ? `${m}m ${rem}s` : `${m}m`;
  const h = Math.floor(m / 60);
  const remM = m % 60;
  if (h < 48) return remM > 0 ? `${h}h ${remM}m` : `${h}h`;
  const d = Math.floor(h / 24);
  const remH = h % 24;
  return remH > 0 ? `${d}d ${remH}h` : `${d}d`;
}

/** Relative age from an ISO timestamp to now. */
export function formatRelativeIso(iso: string | null | undefined): string {
  if (!iso) return '—';
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return '—';
  const sec = Math.max(0, Math.round((Date.now() - t) / 1000));
  return formatDurationSec(sec) + ' ago';
}

/** Timing ms → short label; null stays em-dash (not "0ms"). */
export function formatMs(ms: number | null | undefined): string {
  if (ms == null || !Number.isFinite(ms)) return '—';
  if (ms < 1000) return `${Math.round(ms)}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

/**
 * intent.requested / delivered / fellBackFrom are Mongo values — may be
 * strings, numbers, or objects. Never drop a raw object into JSX.
 */
export function formatUnknown(value: unknown): string {
  if (value == null) return '—';
  if (typeof value === 'string') return value || '—';
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

/** Stage-age colour: greys → orange → red as it approaches the 600s stall bar. */
export function stageAgeColor(stageAgeSec: number | null | undefined, stalled: boolean): string {
  if (stalled) return 'red.600';
  if (stageAgeSec == null) return 'brand.muted';
  if (stageAgeSec > 480) return 'orange.600';
  if (stageAgeSec > 300) return 'orange.500';
  if (stageAgeSec > 120) return 'yellow.700';
  return 'brand.muted';
}

/**
 * Badge colours for Ad.status. `draft` is the success terminal after a
 * completed render (renderService sets status: 'draft') — green so it
 * does not read as a failure. `live` = published; `archived` = retired.
 */
export function statusColorScheme(status: string): string {
  switch (status) {
    case 'draft':
      // Rendered successfully — not a failure state despite the name.
      return 'green';
    case 'live':
      return 'teal';
    case 'archived':
      return 'gray';
    case 'failed':
      return 'red';
    case 'rendering':
      return 'purple';
    case 'queued':
      return 'orange';
    default:
      return 'gray';
  }
}

/**
 * Clipboard with insecure-origin fallback. navigator.clipboard is missing
 * on non-HTTPS and can reject; execCommand is the last-resort path so the
 * operator is never stuck with a dead button during an incident.
 */
export async function copyText(text: string): Promise<void> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return;
    }
  } catch {
    // fall through
  }
  const ta = document.createElement('textarea');
  ta.value = text;
  ta.setAttribute('readonly', '');
  ta.style.position = 'fixed';
  ta.style.left = '-9999px';
  ta.style.top = '0';
  document.body.appendChild(ta);
  try {
    ta.select();
    ta.setSelectionRange(0, ta.value.length);
    if (typeof document.execCommand !== 'function') {
      throw new Error('Clipboard unavailable');
    }
    const ok = document.execCommand('copy');
    if (!ok) throw new Error('Clipboard unavailable');
  } finally {
    document.body.removeChild(ta);
  }
}

/** Join diagnostics for "Copy all" — each block verbatim, blank line + `--- ` between. */
export function joinDiagnostics(diagnostics: string[]): string {
  return diagnostics
    .map((d) => d ?? '')
    .filter((d) => d.length > 0)
    .join('\n\n--- \n\n');
}
